import React, { useEffect, useMemo, useState } from "react";
import {
  Button,
  FlexDirectionRow,
  FormInputLabel,
  FormItemContainer,
  MainContent,
  SubTitle,
} from "../../../../../components/StyledComponents";
import { Box, Grid } from "@mui/material";
import { validateCPF, valueIsRequired } from "../../../../../utils/regex";
import Select from "../../../../../components/Select";
import { useApp } from "../../../../../context/app";
import { useFieldArray, useForm } from "react-hook-form";
import InputControlled from "../../../../../components/InputControlled";
import { AgentesSseService } from "../../../services/agentesSseService";
import {
  capitalizeWords,
  onlyInputNumbers,
  onlyNumbers,
  parseStringToJson,
  removeEmpty,
} from "../../../../../utils/utils";
import Search from "@material-ui/icons/Search";
import styles from "../../../../../components/TableAdmin/styles.module.scss";
import Spinner from "react-spinner-material";
import { handleModalStatus } from "../../../../../utils/handleModalStatus";
import { BaSeDialogContext } from "@base/react";
import { UserActionsContent } from "../../../../../components/UserActionsContent";
import { handleModalRedirect } from "../../../../../utils/handleModalRedirect";
import FiltersModel from "../../../filters.model";
import RadioOrCheckboxGroup from "../../../../../components/RadioOrCheckboxGroup";
import { GoSearch } from "react-icons/go";

import Paper from "@mui/material/Paper";
import InputBase from "@mui/material/InputBase";
import IconButton from "@mui/material/IconButton";
import { cpfMask, retirarMascara } from "../../../../../utils/mask";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import style from "./styles.module.scss";
import { BsPlusCircle } from "react-icons/bs";
import { RiCloseCircleLine } from "react-icons/ri";

const theme = createTheme({
  components: {
    MuiChip: {
      styleOverrides: {
        deleteIcon: {
          color: "red",
          "&:hover": {
            color: "red",
          },
        },
      },
    },
  },
});

export const IncluirAgentesSSE = () => {
  const { paginationFilters } = FiltersModel();

  const { showModal } = React.useContext(BaSeDialogContext);
  const { setRenderIncludeScreen, manageItem, setManageItem } = useApp();

  const {
    getValues,
    control,
    handleSubmit,
    watch,
    reset,
    register,
    setValue,
    formState: { errors, isSubmitting },
    clearErrors,
    setError,
  } = useForm({
    defaultValues: {
      ufs: [],
      cpf: "",
      nome: "",
      codRegistroSas: "",
      treinamento: false,
    },
    criteriaMode: "all",
  });

  const {
    fields: ufsFields,
    append: appendUf,
    remove: removeUf,
  } = useFieldArray({
    control,
    name: "ufs",
    rules: {
      required: "Selecione as informações",
    },
  });

  const formSelectedInfo = watch();
  const selectedInfo = watch("ufs");

  const [options, setOptions] = useState({ tipo: [], uf: [] });
  const [unidades, setUnidades] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingCPF, setIsLoadingCPF] = useState(false);
  const [isLoadingUnidade, setIsLoadingUnidade] = useState(false);
  const [defaultFirstValue, setDefaultFirstValue] = useState("");
  const [searchCPFResult, setSearchCPFResult] = useState(null);
  const [searchUFResult, setSearchUFResult] = useState(null);
  const [listaEmpresaVinculo, setListaEmpresaVinculo] = useState([]);

  const isAlreadyAdded = useMemo(() => {
    const formValues = getValues();
    return selectedInfo?.some(
      (field) =>
        JSON.stringify(field.uf) === formValues.uf &&
        field.descricaoUnidadeOrganizacional ===
          formValues.descricaoUnidadeOrganizacional &&
        field.codUnidadeOrganizacional ===
          formValues.codUnidadeOrganizacional &&
        field.empresaVinculada === formValues.empresaVinculada
    );
    // eslint-disable-next-line
  }, [formSelectedInfo, selectedInfo]);

  useEffect(() => {
    const subscription = watch((value, { name }) => {
      if (name === "uf") {
        setUnidades([]);
        setValue("descricaoUnidadeOrganizacional", "");
        setValue("codUnidadeOrganizacional", "");
        clearErrors([
          "codUnidadeOrganizacional",
          "descricaoUnidadeOrganizacional",
        ]);
      }

      if (name === "cpf") {
        const cpf = watch("cpf");
        const editCPF = cpfMask(manageItem?.data?.cpf);
        if (cpf.length === 14 && editCPF !== cpf) {
          setTimeout(() => {
            handleSearchCPF();
          }, 100);
        }
      }
      if (name === "descricaoUnidadeOrganizacional") {
        const descricaoUnidadeOrganizacionalParsed = parseStringToJson(
          value[name]
        );
        if (descricaoUnidadeOrganizacionalParsed?.codigoUnidadeOrganizacional) {
          setValue(
            "codUnidadeOrganizacional",
            descricaoUnidadeOrganizacionalParsed?.codigoUnidadeOrganizacional
          );
          clearErrors("codUnidadeOrganizacional");
        }
      }
      return value;
    });
    return () => subscription.unsubscribe();
    // https://github.com/facebook/create-react-app/issues/6880
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watch]);

  useEffect(() => {
    async function loadedData() {
      const { data } = manageItem;
      if (data?.id) {
        reset(data);
        setValue("empresaVinculada", "");
        setValue("uf", "");
        setValue("descricaoUnidadeOrganizacional", "");
        setValue("codUnidadeOrganizacional", "");
        setValue("cpf", cpfMask(data.cpf));
        if (data.cpf) {
          await findCPFData();
        }
        setIsLoading(false);
      }
    }

    loadedData();
    // https://github.com/facebook/create-react-app/issues/6880
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [manageItem]);

  const handleEditClick = async (item) => {
    const body = {
      cpf: item.cpf,
    };
    const response = await AgentesSseService.findAll(paginationFilters, body);
    if (!response.content) return;
    const agente = response.content?.find((a) => a.cpf === item.cpf);
    if (!!agente) {
      setRenderIncludeScreen(false);
      setManageItem({
        type: "edit",
        data: agente,
      });
      setRenderIncludeScreen(true);
    }
  };

  const handleExists = (item) => {
    handleModalRedirect({
      title: "Agente SSE",
      content: `Já existe um agente SSE cadastrado com o CPF: ${item.cpf}, deseja edita-lo?`,
      onFinish: () => handleEditClick(item),
      onCancelClick: () => console.log("modal closed..."),
      showModal: showModal,
    });
  };

  const onResetFields = () => {
    reset();
  };

  const getOptions = async () => {
    const response = await AgentesSseService.getFilterOptions(false);
    await setOptions(response);
    if (!manageItem?.data?.id) {
      setIsLoading(false);
    }
  };

  const getUnidades = async () => {
    const uf = parseStringToJson(watch("uf")) || null;
    const codUnidadeOrganizacional = watch("codUnidadeOrganizacional") || null;
    clearErrors(["codUnidadeOrganizacional", "descricaoUnidadeOrganizacional"]);
    if (!uf) {
      setError("codUnidadeOrganizacional", { message: "Selecione uma UF" });
      return;
    }
    try {
      setIsLoadingUnidade(true);
      const response = await AgentesSseService.getUnidades(
        uf.codSebrae,
        codUnidadeOrganizacional
      );

      setUnidades([]);
      if (response) {
        setDefaultFirstValue("Selecione uma unidade");
        if (codUnidadeOrganizacional) {
          const unidadeFiltered = response.find(
            (r) =>
              r?.codigoUnidadeOrganizacional ===
              parseInt(codUnidadeOrganizacional)
          );
          setValue("descricaoUnidadeOrganizacional", unidadeFiltered);
        }
        setUnidades(response);
        setIsLoadingUnidade(false);
      } else {
        setDefaultFirstValue("Nenhuma unidade encontrada");
        setIsLoadingUnidade(false);
      }
      return response;
    } catch (e) {
      setDefaultFirstValue("Nenhuma unidade encontrada");
      setError("descricaoUnidadeOrganizacional", {
        message: `Falha ao tentar localizar as unidades`,
      });
      setIsLoadingUnidade(false);
    }
  };

  const submitForm = async (dataForm) => {
    if (dataForm.uf) {
      dataForm.uf = parseStringToJson(dataForm.uf);
    }

    if (!dataForm?.ufs?.length) {
      setError("ufs", { message: "Selecione os campos obrigatórios" });
      return;
    }
    dataForm = removeEmpty(dataForm);
    dataForm.cpf = onlyNumbers(dataForm.cpf);
    if (dataForm.id) {
      await editItem(dataForm);
      return;
    }
    await createItem(dataForm);
  };

  const handleGoHome = () => {
    setRenderIncludeScreen(false);
  };

  const createItem = async (dataForm) => {
    AgentesSseService.save(dataForm)
      .then((response) => {
        if (response) {
          handleModalStatus({
            type: "success",
            msgOnSuccess: "Agente SSE foi cadastrado com sucesso.",
            onCloseModal: handleGoHome,
            showModal,
          });
        } else {
          handleModalStatus({
            type: "error",
            msgOnError:
              "O agente SSE não foi cadastrado. Aconteceu um erro ou o sistema está temporariamente fora do ar. Tente novamente mais tarde.",
            showModal,
          });
        }
      })
      .catch((error) => {
        console.log("error", error);
        const hasErrorMessage = error?.response?.data?.message || error?.response?.data?.descricao;
        if (hasErrorMessage?.includes("Já existe um atendente nan cadastrado com o cpf")) {
          return handleExists(dataForm);
        }
        handleModalStatus({
          type: "error",
          msgOnError:
            hasErrorMessage ??
            "O agente SSE não foi cadastrado. Aconteceu um erro ou o sistema está temporariamente fora do ar. Tente novamente mais tarde.",
          showModal,
        });
      });
  };

  const editItem = async (dataForm) => {
    AgentesSseService.update(dataForm)
      .then((res) => {
        if (res.data) {
          handleModalStatus({
            type: "success",
            msgOnSuccess: "Agente SSE foi editado com sucesso.",
            onCloseModal: handleGoHome,
            showModal,
          });
        } else {
          handleModalStatus({
            type: "error",
            msgOnError:
              "O agente SSE não foi editado. Aconteceu um erro ou o sistema está temporariamente fora do ar. Tente novamente mais tarde.",
            showModal,
          });
        }
      })
      .catch((error) => {
        const hasErrorMessage = error?.response?.data?.descricao;
        handleModalStatus({
          type: "error",
          msgOnError: hasErrorMessage ??
            "O agente SSE não foi editado. Aconteceu um erro ou o sistema está temporariamente fora do ar. Tente novamente mais tarde.",
          showModal,
        })
      });
  };

  const handleListaVinculo = (listaVinculo) => {
    return listaVinculo
      .filter((i) => Number(i.situacao) === 1)
      .map((l) => ({
        nomeRazaoSocialPJ: l.nomeRazaoSocialPJ,
        descCargCli: l?.descCargCli,
      }))
      .filter((i) => i.nomeRazaoSocialPJ);
  };

  const handleSearchCPF = async () => {
    const cpf = watch("cpf");
    if (cpf.length !== 14) return;
    setIsLoadingCPF(true);
    await AgentesSseService.findCPF(retirarMascara(cpf))
      .then((response) => {
        if (!!response) {
          setIsLoadingCPF(false);
          setSearchCPFResult(response);
          if (response?.listaVinculo?.length) {
            const lista = handleListaVinculo(response.listaVinculo);
            setListaEmpresaVinculo(lista);
          }
          handleUFSearch(response.codSebrae);
          clearErrors();
        } else {
          setIsLoadingCPF(false);
          setSearchCPFResult(null);
          setError("cpf", { message: "CPF não encontrado na base" });
        }
      })
      .catch((err) => {
        console.log("error", err);
        setIsLoadingCPF(false);
        setSearchCPFResult(null);
        setError("cpf", { message: "CPF não encontrado na base" });
      });
  };

  const findCPFData = async () => {
    const cpf = watch("cpf");
    if (cpf.length !== 14) return;
    try {
      const response = await AgentesSseService.findCPF(retirarMascara(cpf));
      if (!!response) {
        setSearchCPFResult(response);
        if (response?.listaVinculo?.length) {
          const lista = handleListaVinculo(response.listaVinculo);
          setListaEmpresaVinculo(lista);
        }
        clearErrors();
      } else {
        if(manageItem.data?.id) {
          setSearchCPFResult(manageItem?.data);
        } else {
          setSearchCPFResult(null);
        }
        setError("cpf", { message: "CPF não encontrado na base" });
      }
    } catch (e) {
      console.log("error", e);
      setIsLoadingCPF(false);
      if(manageItem.data?.id) {
        setSearchCPFResult(manageItem?.data);
      } else {
        setSearchCPFResult(null);
      }
      setError("cpf", { message: "CPF não encontrado na base" });
    }
  };

  const handleUFSearch = async (codSebrae) => {
    await AgentesSseService.findUF(codSebrae)
      .then((response) => {
        setSearchUFResult(response);
      })
      .catch((err) => {
        console.log("error", err);
      });
  };

  useEffect(() => {
    if (searchCPFResult) {
      if (searchCPFResult.nomeRazaoSocial) {
        setValue("nome", capitalizeWords(searchCPFResult.nomeRazaoSocial));
      }
      if (searchCPFResult.codParceiro) {
        setValue("codRegistroSas", searchCPFResult.codParceiro);
      }
    }
    // https://github.com/facebook/create-react-app/issues/6880
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchCPFResult]);

  useEffect(() => {
    if (searchUFResult) {
      setValue("uf", searchUFResult);
    }
    // https://github.com/facebook/create-react-app/issues/6880
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchUFResult]);

  useEffect(() => {
    getOptions();
    // https://github.com/facebook/create-react-app/issues/6880
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleAddNewUf = () => {
    const {
      uf,
      descricaoUnidadeOrganizacional,
      codUnidadeOrganizacional,
      empresaVinculada,
      tipo,
    } = getValues();

    const requiredFields = [
      "uf",
      "descricaoUnidadeOrganizacional",
      "codUnidadeOrganizacional",
      "tipo",
    ];

    if (requiredFields.some((field) => !getValues()[field])) {
      setError("ufs", { message: "Selecione os campos obrigatórios" });
      return;
    }

    const formValues = {
      uf: parseStringToJson(uf),
      descricaoUnidadeOrganizacional: parseStringToJson(
        descricaoUnidadeOrganizacional
      )?.descricao,
      codUnidadeOrganizacional,
      tipo,
    };

    if (empresaVinculada) {
      formValues.empresaVinculada = empresaVinculada;
    }

    const formValuesCopy = { ...formValues };
    appendUf(formValuesCopy);
    clearErrors("ufs");
    setValue("uf", "");
    setValue("descricaoUnidadeOrganizacional", "");
    setValue("codUnidadeOrganizacional", "");
    setValue("empresaVinculada", "");
    setValue("tipo", "");
  };

  return (
    <>
      <MainContent>
        <SubTitle>
          <h3>{manageItem?.data?.id ? "Editar" : "Incluir"}</h3>
        </SubTitle>

        {isLoading ? (
          <div className={styles.spinnerCenter}>
            <Spinner radius={80} color={"#0061C6"} stroke={2} visible={true} />
            <p>Carregando...</p>
          </div>
        ) : (
          <Grid container style={{ paddingBottom: "30px" }}>
            <Grid item container sm={8} md={8}>
              <Grid item md={3}>
                <FormItemContainer>
                  <FormInputLabel>
                    <label>
                      CPF <strong style={{ color: "#0061C6" }}>*</strong>
                    </label>
                  </FormInputLabel>
                  <Paper
                    component="form"
                    sx={{
                      p: "2px 2px",
                      display: "flex",
                      alignItems: "center",
                      width: 400,
                      border: "1px solid",
                      height: "32px",
                      borderRadius: 0,
                      boxShadow: "none",
                    }}
                  >
                    <InputBase
                      id={"input-base"}
                      value={cpfMask(watch("cpf"))}
                      sx={{ ml: 1, flex: 1, border: "none !important" }}
                      {...register("cpf", {
                        required: {
                          value: true,
                          message: "Campo obrigatório",
                        },
                        validate: validateCPF,
                      })}
                      placeholder="999.999.999-99"
                    />
                    <IconButton
                      type="button"
                      sx={{ p: "10px" }}
                      aria-label="search"
                      onClick={handleSearchCPF}
                    >
                      <div>
                        <GoSearch color={"#0061C6"} sixe={25} />
                      </div>
                    </IconButton>
                  </Paper>

                  {!!errors?.cpf && (
                    <p style={{ color: "#F5365C" }}>{errors?.cpf?.message}</p>
                  )}
                </FormItemContainer>
              </Grid>
            </Grid>

            {!!searchCPFResult && !isLoadingCPF && (
              <Grid container>
                <Grid item container sm={8} md={8} spacing={3}>
                  <Grid item md={3}>
                    <InputControlled
                      name="nome"
                      control={control}
                      rules={{ ...valueIsRequired() }}
                      placeholder="Nome"
                      maxLength={140}
                      showMaxLengthLabel={false}
                    />
                  </Grid>

                  <Grid item sm={4} md={4}>
                    <RadioOrCheckboxGroup
                      title="Testador Beta"
                      isBold={true}
                      name="treinamento"
                      control={control}
                      values={[
                        {
                          placeholder: "Sim",
                          value: true,
                        },
                        {
                          placeholder: "Não",
                          value: false,
                        },
                      ]}
                    />
                  </Grid>
                </Grid>
                <Grid item container sm={8} md={8} spacing={3}>
                  {/*<Grid item md={2}>*/}
                  {/*    <Select*/}
                  {/*        name="tipo"*/}
                  {/*        placeholder="Perfil"*/}
                  {/*        options={options.tipo}*/}
                  {/*        control={control}*/}
                  {/*        rules={{...valueIsRequired()}}*/}
                  {/*    />*/}
                  {/*</Grid>*/}
                  <Grid item md={3}>
                    <InputControlled
                      name="codRegistroSas"
                      control={control}
                      rules={{ ...valueIsRequired() }}
                      label="Parceiro SAS"
                      placeholder="ex: 99999999"
                      maxLength={255}
                      showMaxLengthLabel={false}
                    />
                  </Grid>
                </Grid>

                <Grid item container display="flex" alignItems="center">
                  <Grid
                    item
                    container
                    sm={10}
                    md={10}
                    style={{
                      padding: "16px",
                      border: "0.6px solid #e1dfdf",
                    }}
                  >
                    <Grid item container>
                      <Grid item sm={2} md={2} pr={2}>
                        <Select
                          name="uf"
                          returnItem
                          defaultName="sigla"
                          placeholder="UF"
                          options={options.uf}
                          control={control}
                        />
                      </Grid>

                      <Grid item sm={4} md={4} pr={4}>
                        <InputControlled
                          {...{ onKeyPress: onlyInputNumbers }}
                          name="codUnidadeOrganizacional"
                          control={control}
                          label="Código Unidade Organizacional"
                          placeholder="Ex.: 87142419004205"
                          maxLength={20}
                          showMaxLengthLabel={false}
                          icon={() => (
                            <Search
                              htmlColor={"#0061C6"}
                              onClick={() => getUnidades()}
                            />
                          )}
                        />
                      </Grid>

                      <Grid item sm={4} md={4}>
                        <Select
                          isLoading={isLoadingUnidade}
                          defaultName={"descricao"}
                          name="descricaoUnidadeOrganizacional"
                          returnItem
                          label="Descrição Unidade Organizacional"
                          defaultFirstValue={defaultFirstValue}
                          options={unidades}
                          control={control}
                        />
                      </Grid>
                    </Grid>
                    <Grid item container spacing={2}>
                      <Grid item md={3}>
                        <Select
                          notRequired={true}
                          placeholderValue={null}
                          defaultName={"nomeRazaoSocialPJ"}
                          defaultValue={"nomeRazaoSocialPJ"}
                          name="empresaVinculada"
                          placeholder="Empresa Habilitada"
                          options={listaEmpresaVinculo}
                          control={control}
                        />
                      </Grid>
                    </Grid>

                    <Grid item container spacing={2}>
                      <Grid item md={3}>
                        <Select
                          name="tipo"
                          placeholder="Perfil"
                          options={options.tipo}
                          control={control}
                        />
                      </Grid>
                    </Grid>
                  </Grid>
                  <button
                    onClick={() => handleAddNewUf()}
                    disabled={isAlreadyAdded}
                    type="button"
                    style={{
                      marginLeft: "10px",
                      border: "none",
                      background: "none",
                      cursor: "pointer",
                    }}
                  >
                    <BsPlusCircle
                      size={30}
                      color={isAlreadyAdded ? "#9ca2a8" : "#0061C6"}
                    />
                  </button>
                </Grid>
                <Grid container item py={2}>
                  {!!(errors?.ufs ?? errors?.ufs?.root) && (
                    <p style={{ color: "#F5365C" }}>
                      {errors?.ufs?.message ?? errors?.ufs?.root?.message}
                    </p>
                  )}
                </Grid>

                <Box
                  display="flex"
                  flexDirection={"column"}
                  gap="0.5rem"
                  flexWrap="wrap"
                  mt={3}
                >
                  <ThemeProvider theme={theme}>
                    {ufsFields.map((field, index) => {
                      return (
                        <div key={index} className={style.chipUfs}>
                          <div className={style.chipText}>
                            <p>
                              {field?.tipo ? (
                                <>
                                  <strong>{field?.tipo}</strong> -{" "}
                                </>
                              ) : null}
                              <strong>{field?.uf?.sigla}</strong> -{" "}
                              {field?.codUnidadeOrganizacional}
                            </p>
                            {!!field?.empresaVinculada && (
                              <p>{field?.empresaVinculada} </p>
                            )}
                          </div>
                          <div style={{ marginRight: 10 }}>
                            <RiCloseCircleLine
                              onClick={() => removeUf(index)}
                              size={19}
                              color={"#FF0000"}
                            />
                          </div>
                        </div>
                      );
                    })}
                  </ThemeProvider>
                </Box>
              </Grid>
            )}

            <Grid container justifyContent="flex-end">
              <Grid item>
                <FlexDirectionRow position={"end"}>
                  {!manageItem?.data?.id && (
                    <Button
                      href="#"
                      backgroundColor={"#9CA2A8"}
                      onClick={onResetFields}
                    >
                      Limpar
                    </Button>
                  )}

                  <div style={{ marginLeft: "10px", marginRight: "10px" }} />
                  <Button href="#" onClick={handleSubmit(submitForm)} disabled={isSubmitting} >
                    {manageItem?.data?.id ? "Editar" : "Incluir"} agente SSE
                  </Button>
                </FlexDirectionRow>
              </Grid>
            </Grid>
          </Grid>
        )}
        {isLoadingCPF && (
          <div className={styles.spinnerCenter}>
            <Spinner radius={80} color={"#0061C6"} stroke={2} visible={true} />
            <p>Carregando...</p>
          </div>
        )}
      </MainContent>
      {manageItem?.data?.id && !isLoading && (
        <UserActionsContent data={manageItem?.data} />
      )}
    </>
  );
};
